/**
 * iter350 — Saisie du temps de travail réellement passé (atelier).
 * Heures + minutes, plusieurs périodes, correction avec historique.
 */
import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clock, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const fmt = (min) => `${Math.floor((min || 0) / 60)} h ${String((min || 0) % 60).padStart(2, "0")}`;

export const TempsDialog = ({ open, onClose, orId, onSaved }) => {
  const [h, setH] = useState("");
  const [m, setM] = useState("");
  const [commentaire, setCommentaire] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    if (!open || !orId) return;
    const { data } = await api.get(`/or/${orId}/temps`);
    setData(data);
  }, [open, orId]);

  useEffect(() => { load(); }, [load]);

  const enregistrer = async () => {
    const heures = parseInt(h || "0", 10) || 0;
    const minutes = parseInt(m || "0", 10) || 0;
    if (heures * 60 + minutes <= 0) { toast.error("Indique une durée"); return; }
    setBusy(true);
    try {
      if (edit) {
        await api.patch(`/or/temps/${edit.id}`, { heures, minutes, motif: commentaire || null });
        toast.success("Temps corrigé");
      } else {
        await api.post(`/or/${orId}/temps`, { heures, minutes, commentaire: commentaire || null });
        toast.success("Temps enregistré");
      }
      setH(""); setM(""); setCommentaire(""); setEdit(null);
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  const demarrerCorrection = (p) => {
    setEdit(p);
    setH(String(Math.floor(p.minutes / 60)));
    setM(String(p.minutes % 60));
    setCommentaire("");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="temps-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Clock className="w-4 h-4 text-[#52a675]" />
            {edit ? "Corriger le temps" : "Temps passé sur ce dossier"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Heures</Label>
            <Input
              type="number" min="0" inputMode="numeric" value={h} placeholder="0"
              onChange={(e) => setH(e.target.value)}
              className="h-14 text-2xl text-center font-mono"
              data-testid="temps-heures"
            />
          </div>
          <div className="pb-4 text-xl text-gray-400">:</div>
          <div className="flex-1">
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Minutes</Label>
            <Input
              type="number" min="0" max="59" step="5" inputMode="numeric" value={m} placeholder="00"
              onChange={(e) => setM(e.target.value)}
              className="h-14 text-2xl text-center font-mono"
              data-testid="temps-minutes"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {[[0, 30], [1, 0], [1, 30], [2, 0], [3, 0]].map(([hh, mm]) => (
            <button
              key={`${hh}-${mm}`}
              onClick={() => { setH(String(hh)); setM(String(mm)); }}
              className="px-3 py-1.5 rounded-full border border-gray-200 text-xs hover:border-[#52a675] hover:text-[#2d5f3f] transition-colors"
              data-testid={`temps-raccourci-${hh}h${mm}`}
            >
              {hh} h {mm ? String(mm).padStart(2, "0") : ""}
            </button>
          ))}
        </div>

        <Input
          value={commentaire}
          onChange={(e) => setCommentaire(e.target.value)}
          placeholder={edit ? "Motif de la correction (facultatif)" : "Commentaire (facultatif)"}
          className="h-10"
          data-testid="temps-commentaire"
        />

        {(data?.items || []).length > 0 && (
          <div className="border-t border-gray-100 pt-3 space-y-1.5 max-h-48 overflow-auto">
            <div className="text-[11px] uppercase tracking-wider text-gray-500">
              Déjà saisi — total {fmt(data.total_minutes)}
            </div>
            {data.items.map((p) => (
              <div key={p.id} className="flex items-center gap-2 text-xs" data-testid={`temps-periode-${p.id}`}>
                <span className="font-mono font-semibold text-gray-800">{fmt(p.minutes)}</span>
                <span className="text-gray-500 truncate flex-1">
                  {p.date}{p.commentaire ? ` · ${p.commentaire}` : ""}
                  {(p.corrections || []).length > 0 ? " · corrigé" : ""}
                </span>
                <button
                  onClick={() => demarrerCorrection(p)}
                  className="text-gray-400 hover:text-[#52a675]"
                  data-testid={`temps-corriger-${p.id}`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => { setEdit(null); onClose?.(); }} data-testid="temps-annuler">
            Fermer
          </Button>
          <Button
            onClick={enregistrer} disabled={busy}
            className="bg-[#52a675] hover:bg-[#2d5f3f]"
            data-testid="temps-enregistrer"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : edit ? "Corriger" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TempsDialog;
